import {
    ClickEvent, DataSource, DataSourceAction, DataSourceExecutionEvent, Image, ListItem, ModelDataChangeType, Overlay,
    Snackbar, TableRow, TableRowDisplayEvent, TableSelectionEvent, Textbox
} from "@mcleod/components";
import { Api, Currency, CurrencySettings, ModelRow } from "@mcleod/core";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { RowOfficePayrollProfile } from "@mcleod/settlement/officepayroll/src/models/ModelOfficePayrollProfile";
import { AutogenLayoutOfficePayrollProcessingGroupBy } from "./autogen/AutogenLayoutOfficePayrollProcessingGroupBy";
import { OfficePayrollProcessingFilter } from "./OfficePayrollProcessingFilter";
import { OfficePayeeData, OfficePayeeDataLookup } from "@mcleod/settlement/src/PayeeData";
import { OfficePayrollProcessingPayeeDetail } from "./OfficePayrollProcessingPayeeDetail";
import { PayablesLookup } from "@mcleod/settlement/src/OrderPayables";

export interface OfficePayeeSelectedProps {
    payeeName: string;
    payeeId: string;
    payeeData: OfficePayeeData;
    profileRow: RowOfficePayrollProfile;
}

export class OfficePayrollProcessingGroupBy extends AutogenLayoutOfficePayrollProcessingGroupBy {
    private payeeDetailLayout: OfficePayrollProcessingPayeeDetail;
    private profileFilter: OfficePayrollProcessingFilter;
    public lookupData: PayablesLookup;
    public payeeDataLookup: OfficePayeeDataLookup;
    public profileRow: RowOfficePayrollProfile;
    public onPayeeSelected?: (props: OfficePayeeSelectedProps) => void;
    public onPayeeDeselected?: () => void;
    private payeeCache: DataSource;
    private updateTimecardButton: () => void;
    private updateProcessButton: () => void;
    private syncTimecards: () => void;
    // Tracks all payee IDs with non-null process status
    public acceptedPayeeIds: Set<string> = new Set();

    override onLoad(): void {
        this.buttonAcceptUnaccept.addClickListener(async (event: ClickEvent) => {
            const selectedRow = this.tableGroupBy.selectedRow;
            if (selectedRow == null) {
                return;
            }
            try {
                if (selectedRow.data.isNull("process_status")) {
                    await this.acceptPayees();
                } else {
                    await this.unacceptPayees();
                }
                // Note: Button will be set to not busy in setAcceptButtonProps when imageAccepted is updated
            } catch (error) {
                console.error("Error during accept/unaccept operation:", error);
            }
        });
        this.buttonEllipsis.addClickListener(async (event: ClickEvent) => {
            const dropdownItems = [
                new ListItem({
                    text: "Accept All",
                    onClick: async () => {
                        await this.acceptPayees(true);
                    }
                }),
                new ListItem({
                    text: "Unaccept All",
                    onClick: async () => {
                        await this.unacceptPayees(true);
                    }
                })
            ];

            Overlay.showDropdown(this.buttonEllipsis, dropdownItems, null, { width: 120 });
        });
    }

    async initialize(payeeDetailLayout: OfficePayrollProcessingPayeeDetail, profileFilter: OfficePayrollProcessingFilter, payeeDataLookup: OfficePayeeDataLookup, profileRow: RowOfficePayrollProfile, payeeCache: DataSource, updateTimecardButton: () => void, updateProcessButton: () => void, syncTimecards: () => void): Promise<void> {
        return new Promise<void>(async (resolve) => {
            this.payeeDetailLayout = payeeDetailLayout;
            this.profileFilter = profileFilter;
            this.lookupData = profileFilter.payablesLookupMap;
            this.payeeDataLookup = payeeDataLookup;
            this.profileRow = profileRow;
            this.payeeCache = payeeCache;
            this.updateTimecardButton = updateTimecardButton;
            this.updateProcessButton = updateProcessButton;
            this.syncTimecards = syncTimecards;
            this.setCustomFilter();
            if (this.payeeCache.isSearching) {
                this.tableGroupBy.busy = true;
                this.payeeCache.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
                    if (event.getAction() === DataSourceAction.SEARCH) {
                        this.loadRows();
                        resolve();
                    }
                });
            } else {
                await this.loadRows();
                resolve();
            }
        });
    }

    private async loadRows(): Promise<void> {
        this.tableGroupBy.busy = true;
        for (const row of this.payeeCache.data) {
            if (this.profileFilter.dataPassesPayee(row)) {
                this.payeeDataLookup.addPayeeRow(row);
            }
        }
        await this.showAllPayees();
        this.syncTimecards();
        // Only loop through all rows on initial load
        this.acceptedPayeeIds.clear();
        for (const row of this.tableGroupBy.rows) {
            if (!row.data.isNull("process_status")) {
                this.acceptedPayeeIds.add(row.data.get("payee_id"));
            }
        }
        this.updateAcceptedCountLabel();
        this.updateProcessButton();
        this.updateTimecardButton();
        this.buttonEllipsis.visible = this.tableGroupBy.rows.length > 0;
        this.tableGroupBy.busy = false;
    }

    private async showAllPayees(): Promise<void> {
        this.clearTableSelection();
        this.tableGroupBy.displayComponentData(null, [], 0);

        try {
            // Process existing data to get counts and totals for payees with records
            const existingPayeesMap = await this.processExistingPayableData();

            // Create rows for all payees
            const rows = [];
            for (const payeeData of this.payeeDataLookup.getAllPayeeData()) {
                const payeeRow = payeeData.getPayeeRow();
                if (payeeRow == undefined || payeeRow.get("payee_status") === "N") continue;
                const hasNoBonusCodeAdjustments = !existingPayeesMap.has(payeeRow.get("payee_id")) && !this.profileRow.isNull("bonus_code");
                if (hasNoBonusCodeAdjustments) {
                    continue;
                }
                const payee_id = payeeRow.get("payee_id");
                const row = await this.createPayeeRow(payeeRow, existingPayeesMap.get(payee_id));
                rows.push(row);
            }

            this.displayGroupByData(rows);
            this.tableGroupBy.applySort("payee_name", "asc");
        } catch (error) {
            console.error("Error fetching all payees:", error);
        }
    }

    updateAcceptedCountLabel(): void {
        this.labelAcceptedCount.text = `Accepted (${this.acceptedPayeeIds.size}/${this.tableGroupBy.rows.length})`;
    }

    private clearTableSelection(): void {
        if (this.tableGroupBy.selectedRow) {
            this.tableGroupBy.selectedRow = null;
        }
    }

    private async processExistingPayableData(): Promise<Map<string, {
        count: number;
        process_count: number;
        hold_total: Currency;
        process_total: Currency;
        payee_name: string;
        freeze_by: string;
        process_status: string;
        payee_status: string;
    }>> {
        const existingPayeesMap: Map<string, {
            count: number;
            process_count: number;
            hold_total: Currency;
            process_total: Currency;
            payee_name: string;
            freeze_by: string;
            process_status: string;
            payee_status: string;
        }> = new Map();

        for (const row of this.sourceOrderPayablesCache.data) {
            if (!this.profileFilter.dataPasses(row)) {
                continue; // Skip rows that do not pass the filter
            }
            this.payeeDataLookup.addPayable(row);
            this.lookupData.addData(row);
            const payee_id = row.get("payee_id");
            if (!existingPayeesMap.has(payee_id)) {
                existingPayeesMap.set(payee_id, {
                    count: 0,
                    process_count: 0,
                    hold_total: this.createFilteredCurrency(),
                    process_total: this.createFilteredCurrency(),
                    payee_name: row.get("payee_name", null),
                    freeze_by: row.get("freeze_by", null),
                    process_status: null,
                    payee_status: row.get("payee_status", null)
                });
            }
            existingPayeesMap.get(payee_id).count++;
            const totalPay = row.getCurrency("pay_amount", 0);
            if (row.get("ready_to_pay_flag") == "Y") {
                existingPayeesMap.get(payee_id).process_count++;
                await this.addCurrency(existingPayeesMap.get(payee_id).process_total, totalPay);
            } else {
                await this.addCurrency(existingPayeesMap.get(payee_id).hold_total, totalPay);
            }
        }

        return existingPayeesMap;
    }

    private async createPayeeRow(payeeRow: ModelRow, existingData?: {
        count: number;
        process_count: number;
        hold_total: Currency;
        process_total: Currency;
        payee_name: string;
        freeze_by: string;
        process_status: string;
    }): Promise<ModelRow> {
        const payee_id = payeeRow.get("payee_id");
        const payee_name = payeeRow.get("payee_name");
        const row = new ModelRow("", false);
        row.set("payee_id", payee_id);
        row.set("payee_name", payee_name);
        row.setLookupModelData("payee_id", { id: payee_id, name: payee_name });

        if (existingData) {
            // Payee has existing records
            row.set("count", existingData.count);
            row.set("process_count", existingData.process_count);
            row.set("hold_total", existingData.hold_total);
            row.set("process_total", existingData.process_total);
            row.set("freeze_by", payeeRow.get("freeze_by"));
            row.set("process_status", payeeRow.get("process_status"));
        } else {
            // Payee has no records, set zero values
            row.set("count", 0);
            row.set("process_count", 0);
            row.set("hold_total", this.createFilteredCurrency());
            row.set("process_total", this.createFilteredCurrency());
            row.set("freeze_by", payeeRow.get("freeze_by"));
            row.set("process_status", payeeRow.get("process_status"));
        }

        if (!payeeRow.isNull("tc_total_pay") && this.profileRow.isNull("bonus_code")) {
            await this.addCurrency(row.get("process_total"), payeeRow.getCurrency("tc_total_pay"));
        }

        return row;
    }

    private displayGroupByData(rows: ModelRow[]): void {
        this.tableGroupBy.displayComponentData(null, rows, 0);
    }

    setCustomFilter() {
        this.payeeDetailLayout.filterPanel.applyRemoveFilter = (filter) => {
            this.payeeDetailLayout.tablePayAdjustments.customFilter = this.getGroupByCustomFilter(this.payeeDetailLayout);
        };
        this.payeeDetailLayout.filterPanel.updateFilterCounts = async () => {
            if (this.payeeDetailLayout.filterPanel.filters.length == 0) return;

            // reset counts
            for (const filter of this.payeeDetailLayout.filterPanel.filters) {
                filter.count = 0;
                filter.button.caption = `${filter.caption} ${filter.count}`;
            }
            const tablePayAdjustments = this.payeeDetailLayout.tablePayAdjustments;
            const currencySettings = CurrencySettings.getSingleton();
            for (const tableRow of tablePayAdjustments.allRows) {
                const modelRow = tableRow.data as ModelRow;
                const selectedRow = this.tableGroupBy.selectedRow;
                const groupByMatches = selectedRow?.data.get("payee_id") == modelRow.get("payee_id");
                for (const filter of this.payeeDetailLayout.filterPanel.filters) {
                    if (filter.filter(modelRow) && this.shouldDisplayRow(modelRow)) {
                        if (!currencySettings.multiCurrencyEnabled() || modelRow.getCurrency("pay_amount").getCurrencyCode() == this.profileFilter.getFilterCurrencyType()) {
                            filter.count++;
                            filter.button.caption = `${filter.caption} ${filter.count}`;
                        }
                    }
                }
            }
        };
        this.payeeDetailLayout.tablePayAdjustments.customFilter = this.getGroupByCustomFilter(this.payeeDetailLayout);
        this.payeeDetailLayout.filterPanel.updateFilterCounts();
    }

    refreshCustomFilter() {
        this.payeeDetailLayout.tablePayAdjustments.customFilter = this.getGroupByCustomFilter(this.payeeDetailLayout);
        this.payeeDetailLayout.filterPanel.updateFilterCounts();
    }

    tableGroupByOnSelect(event: TableSelectionEvent) {
        this.refreshCustomFilter();
        this.setEmptyCaptionForTables(event);
        this.setAcceptButtonProps(event.newRow);
        const selectedRow = event.newRow;
        if (selectedRow) {
            this.loadCalculatedData(selectedRow);
            // Populate timecard and pay adjustments tables with selected payee's info
            const payeeData = this.payeeDataLookup.getPayeeData(selectedRow.data.get("payee_id"));
            this.payeeDetailLayout.populateTimecardForPayee(payeeData);
        } else {
            this.onPayeeDeselected();
            this.payeeDetailLayout.populateTimecardForPayee(null);
        }
    }

    private setEmptyCaptionForTables(event: TableSelectionEvent) {
        if (event.newRows.length > 0 && event.oldRows.length == 0) {
            this.payeeDetailLayout.tablePayAdjustments.emptyCaptionAfterSearch = "No records found";
            this.payeeDetailLayout.tableTimecard.emptyCaptionAfterSearch = "No records found";
        } else if (event.newRows.length == 0) {
            this.payeeDetailLayout.tablePayAdjustments.emptyCaptionAfterSearch = "Please select a Payee from the Group-By table";
            this.payeeDetailLayout.tableTimecard.emptyCaptionAfterSearch = "Please select a Payee from the Group-By table";
        }
    }

    private loadCalculatedData(tableRow: TableRow) {
        if (tableRow == null) return;

        this.onPayeeSelected?.({
            payeeName: tableRow.data.get("payee_name"),
            payeeId: tableRow.data.get("payee_id"),
            payeeData: this.payeeDataLookup.getPayeeData(tableRow.data.get("payee_id")),
            profileRow: this.profileRow
        });
    }

    tableGroupByOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const labelGroupByValue = tableRow.findComponentById("labelGroupByValue") as Textbox;
        CommonTooltips.setTooltipContentLayout(labelGroupByValue, null, "lme/settlement/officepayroll/OfficePayeeQuickInfo", null, { id: modelRow.get("payee_id") });
        const imageFrozenPayable = tableRow.findComponentById("imageFrozenPayable") as Image;
        if (imageFrozenPayable != null) {
            imageFrozenPayable.visible = !modelRow.isNull("freeze_by");
        }
        this.setAcceptButtonProps(tableRow);
    }

    getGroupByCustomFilter(layout: OfficePayrollProcessingPayeeDetail): (row: TableRow) => boolean {
        const activeTaskFilter = layout.filterPanel.getActiveFilter();

        // Default filter behavior for other layouts
        return (row: TableRow) => {
            const taskFilterMatches: boolean = activeTaskFilter == null || activeTaskFilter.filter(row.data);
            return taskFilterMatches && this.shouldDisplayRow(row.data);
        };
    }

    shouldDisplayRow(row: ModelRow): boolean {
        const selectedRow = this.tableGroupBy.selectedRow;
        const groupByMatches: boolean = selectedRow?.data.get("payee_id") == row.get("payee_id");
        return this.profileFilter.dataPasses(row) && groupByMatches;
    }

    async updateGroupByTableByPayable(oldData: ModelRow, newData: ModelRow, changeType: ModelDataChangeType) {
        const payeeId = oldData?.get("payee_id") || newData.get("payee_id");
        const groupByRow = this.tableGroupBy.filteredRows.find(row => row.data.get("payee_id") == payeeId);
        if (changeType == ModelDataChangeType.UPDATE && oldData == null) {
            await this.handleAdd("payable", newData);
        } else if (changeType == ModelDataChangeType.UPDATE && groupByRow && oldData) {
            await this.handleUpdate("payable", oldData, newData, groupByRow);
        } else if (changeType == ModelDataChangeType.DELETE && groupByRow) {
            await this.handleDelete("payable", oldData, newData, groupByRow);
        }
    }

    async updateGroupByTableByPayee(oldData: ModelRow, newData: ModelRow, changeType: ModelDataChangeType) {
        const payeeId = newData.get("payee_id") || oldData?.get("payee_id");
        const groupByRow = this.tableGroupBy.filteredRows.find(row => row.data.get("payee_id") == payeeId);
        this.payeeDataLookup.addPayeeRow(newData);
        if (newData.get("payee_status") == "N" && groupByRow) {
            changeType = ModelDataChangeType.DELETE;
        }
        if (changeType == ModelDataChangeType.UPDATE && groupByRow == null) {
            await this.handleAdd("payee", newData);
        } else if (changeType == ModelDataChangeType.UPDATE && groupByRow && oldData) {
            await this.handleUpdate("payee", oldData, newData, groupByRow);
        } else if (changeType == ModelDataChangeType.DELETE && groupByRow) {
            await this.handleDelete("payee", oldData, newData, groupByRow);
        }
        this.updateAcceptedCountLabel();
        this.updateProcessButton();
    }

    async handleDelete(updateType: "payee" | "payable", oldData: ModelRow, newData: ModelRow, groupByRow: TableRow) {
        if (updateType === "payee") {
            this.payeeDataLookup.deletePayeeRow(newData);
            // this.lookupData.deleteData(newData);
            this.tableGroupBy.removeRow(groupByRow.index);
            // Remove from acceptedPayeeIds if process_status is now null
            if (groupByRow.data.isNull("process_status")) {
                this.acceptedPayeeIds.delete(groupByRow.data.get("payee_id"));
            }
        } else if (updateType === "payable" && oldData) {
            groupByRow.data.set("count", groupByRow.data.get("count") - 1);
            this.payeeDataLookup.deletePayable(oldData);
            // this.lookupData.deleteData(oldData);
            if (oldData.getString("ready_to_pay_flag") == "Y") {
                // Decrement process_count for Process status records
                groupByRow.data.set("process_count", groupByRow.data.get("process_count") - 1);
                await this.subCurrency(groupByRow.data.get("process_total"), oldData.getCurrency("pay_amount"));
                this.tableGroupBy.redisplaySingleRow(groupByRow.index, groupByRow.data, false, false);
            } else if (oldData.getString("ready_to_pay_flag") == "N") {
                await this.subCurrency(groupByRow.data.get("hold_total"), oldData.getCurrency("pay_amount"));
                this.tableGroupBy.redisplaySingleRow(groupByRow.index, groupByRow.data, false, false);
            }
        }

    }

    async handleAdd(updateType: "payee" | "payable", newData: ModelRow) {
        if (updateType === "payee") {
            this.payeeDataLookup.addPayeeRow(newData);
            await this.createNewGroupByRow(updateType, newData);
            // Add to acceptedPayeeIds if process_status is not null
            if (!newData.isNull("process_status")) {
                this.acceptedPayeeIds.add(newData.get("payee_id"));
            }
        } else {
            // this.lookupData.addData(newData);
            this.payeeDataLookup.addPayable(newData);
            const findRow = this.tableGroupBy.allRows.find(row => row.data.get("payee_id") == newData.get("payee_id"));
            if (findRow == null) {
                await this.createNewGroupByRow(updateType, newData);
            } else {
                findRow.data.set("count", findRow.data.get("count") + 1);
                if (newData.getString("ready_to_pay_flag") == "Y") {
                    // Increment process_count for Process status records
                    findRow.data.set("process_count", findRow.data.get("process_count") + 1);
                    await this.addCurrency(findRow.data.get("process_total"), newData.getCurrency("pay_amount"));
                } else {
                    await this.addCurrency(findRow.data.get("hold_total"), newData.getCurrency("pay_amount"));
                }
                this.tableGroupBy.redisplaySingleRow(findRow.index, findRow.data, false, false);
            }
        }

    }

    async handleUpdate(updateType: "payee" | "payable", oldData: ModelRow, newData: ModelRow, groupByRow: TableRow) {
        if (oldData.get("payee_id") == newData.get("payee_id")) {
            if (updateType === "payable") {
                if (oldData.getString("ready_to_pay_flag") == "Y" && newData.getString("ready_to_pay_flag") == "Y") {
                    await this.subCurrency(groupByRow.data.get("process_total"), oldData.getCurrency("pay_amount"));
                    await this.addCurrency(groupByRow.data.get("process_total"), newData.getCurrency("pay_amount"));
                } else if (oldData.getString("ready_to_pay_flag") == "N" && newData.getString("ready_to_pay_flag") == "N") {
                    await this.subCurrency(groupByRow.data.get("hold_total"), oldData.getCurrency("pay_amount"));
                    await this.addCurrency(groupByRow.data.get("hold_total"), newData.getCurrency("pay_amount"));
                } else if (oldData.getString("ready_to_pay_flag") == "Y" && newData.getString("ready_to_pay_flag") == "N") {
                    // Moving from Process to Hold: decrement process_count
                    groupByRow.data.set("process_count", groupByRow.data.get("process_count") - 1);
                    await this.subCurrency(groupByRow.data.get("process_total"), oldData.getCurrency("pay_amount"));
                    await this.addCurrency(groupByRow.data.get("hold_total"), newData.getCurrency("pay_amount"));
                } else if (oldData.getString("ready_to_pay_flag") == "N" && newData.getString("ready_to_pay_flag") == "Y") {
                    // Moving from Hold to Process: increment process_count
                    groupByRow.data.set("process_count", groupByRow.data.get("process_count") + 1);
                    await this.subCurrency(groupByRow.data.get("hold_total"), oldData.getCurrency("pay_amount"));
                    await this.addCurrency(groupByRow.data.get("process_total"), newData.getCurrency("pay_amount"));
                }
                // this.lookupData.addData(newData);
                this.payeeDataLookup.updatePayable(newData);
            } else if (updateType === "payee") {
                if (oldData.get("freeze_by") != newData.get("freeze_by")) {
                    groupByRow.data.set("freeze_by", newData.get("freeze_by"));
                }
                if (oldData.get("payee_name") != newData.get("payee_name")) {
                    groupByRow.data.set("payee_name", newData.get("payee_name"));
                }
                if (oldData.getString("process_status") != newData.getString("process_status")) {
                    groupByRow.data.set("process_status", newData.get("process_status"));
                }
                if (oldData.getString("payee_status") != newData.getString("payee_status")) {
                    groupByRow.data.set("payee_status", newData.get("payee_status"));
                }
                if (this.profileRow.isNull("bonus_code") && oldData.getCurrency("tc_total_pay")?.amount != newData.getCurrency("tc_total_pay")?.amount) {
                    const oldTcTotalPay = oldData.isNull("tc_total_pay") ? 0 : oldData.getCurrency("tc_total_pay").amount;
                    const newTcTotalPay = newData.isNull("tc_total_pay") ? 0 : newData.getCurrency("tc_total_pay").amount;
                    const difference = newTcTotalPay - oldTcTotalPay;
                    if (difference != 0) {
                        await this.addCurrency(groupByRow.data.get("process_total"), new Currency({amount: difference, currency_code: this.profileFilter.getFilterCurrencyType()}));
                    }
                }
                this.payeeDataLookup.addPayeeRow(newData);
                this.payeeDataLookup.getPayeeData(newData.get("payee_id")).hasTaxUpdates = true;
                // Update acceptedPayeeIds based on process_status change
                const payeeId = newData.get("payee_id");
                if (!newData.isNull("process_status")) {
                    this.acceptedPayeeIds.add(payeeId);
                } else {
                    this.acceptedPayeeIds.delete(payeeId);
                }
            }
            this.tableGroupBy.redisplaySingleRow(groupByRow.index, groupByRow.data, false, false);
        } else {
            this.handleDelete(updateType, oldData, newData, groupByRow);
            this.handleAdd(updateType, newData);
        }
    }

    async createNewGroupByRow(updateType: "payee" | "payable", newData: ModelRow) {
        const newRow = new ModelRow("", false);
        newRow.set("payee_id", newData.get("payee_id"));
        newRow.set("payee_name", newData.get("payee_name"));
        newRow.set("count", updateType === "payable" ? 1 : 0);
        newRow.set("process_count", newData.getString("ready_to_pay_flag") == "Y" ? 1 : 0);
        newRow.set("hold_total", newData.getString("ready_to_pay_flag") == "N" ? newData.getCurrency("pay_amount") : this.createFilteredCurrency());
        newRow.set("process_total", newData.getString("ready_to_pay_flag") == "Y" ? newData.getCurrency("pay_amount") : this.createFilteredCurrency());
        if (updateType === "payee" && !newData.isNull("tc_total_pay") && this.profileRow.isNull("bonus_code")) {
            await this.addCurrency(newRow.get("process_total"), newData.getCurrency("tc_total_pay"));
        }
        newRow.set("freeze_by", newData.get("freeze_by"));
        newRow.set("process_status", newData.get("payee_process_status"));
        this.tableGroupBy.addRow(newRow, null, { display: true, addToData: true });
    }

    createFilteredCurrency(): Currency {
        const currencySettings = CurrencySettings.getSingleton();
        const multicurrencyEnabled = currencySettings.multiCurrencyEnabled();

        if (!multicurrencyEnabled) return new Currency(0);

        return new Currency({ currency_code: this.profileFilter.getFilterCurrencyType(), amount: 0 });
    }

    async addCurrency(total: Currency, newCurrency: Currency) {
        const filterCurrency = this.profileFilter.getFilterCurrencyType();
        if (newCurrency && (filterCurrency == newCurrency?.getCurrencyCode() || filterCurrency == null)) {
            await total.add(newCurrency);
        }
    }

    async subCurrency(total: Currency, newCurrency: Currency) {
        const filterCurrency = this.profileFilter.getFilterCurrencyType();
        if (newCurrency && (filterCurrency == newCurrency?.getCurrencyCode() || filterCurrency == null)) {
            await total.subtract(newCurrency);
        }
    }

    public getProfileFilter(): OfficePayrollProcessingFilter {
        return this.profileFilter;
    }

    private async acceptPayees(acceptAll?: boolean): Promise<void> {
        this.setButtonsBusy(true);
        let payeeIds: string[] = [];
        if (acceptAll) {
            payeeIds = this.tableGroupBy.allRows.filter(row => {
                const payeeRow = this.payeeDataLookup.getPayeeData(row.data.get("payee_id"))?.getPayeeRow();
                return row.data.isNull("freeze_by") && row.data.isNull("process_status") && (row.data.get("process_count") > 0 || payeeRow?.getCurrency("tc_total_pay")?.isPositive() === true);
            }).map(row => row.data.get("payee_id"));
        } else {
            const selectedRow = this.tableGroupBy.selectedRow;
            if (!selectedRow) {
                return;
            }
            payeeIds.push(selectedRow.data.get("payee_id"));
        }
        if (payeeIds.length === 0) {
            Snackbar.showWarningSnackbar("No payees can be accepted.");
            this.setButtonsBusy(false);
            return;
        }

        // Validate deductions vs gross pay and separate valid/invalid payees
        const validPayeeIds: string[] = [];
        const invalidPayeeIds: string[] = [];
        const validationErrors: string[] = [];

        for (const payeeId of payeeIds) {
            const payeeValidationErrors = await this.validateDeductionsVsGrossPay([payeeId]);
            if (payeeValidationErrors.length > 0) {
                invalidPayeeIds.push(payeeId);
                validationErrors.push(...payeeValidationErrors);
            } else {
                validPayeeIds.push(payeeId);
            }
        }

        // Handle validation results based on number of payees being processed
        if (payeeIds.length === 1) {
            // Single payee: show snackbar if validation fails
            if (invalidPayeeIds.length > 0) {
                const errorMessage = validationErrors.join("\n\n");
                Snackbar.showWarningSnackbar(errorMessage);
                this.setButtonsBusy(false);
                return;
            }
        } else {
            // Multiple payees: skip invalid ones but continue with valid ones
            if (validPayeeIds.length === 0) {
                // All payees failed validation
                Snackbar.showWarningSnackbar("No payees can be accepted due to validation errors.");
                this.setButtonsBusy(false);
                return;
            }
        }

        // Proceed with API call for valid payees
        if (validPayeeIds.length > 0) {
            try {
                const response = await Api.post("lme/backoffice/officepayroll/accept-payee", {
                    payee_ids: validPayeeIds.join(",")
                });

                // Update UI for successfully accepted payees
                if (response?.data?.[0]?.accepted_payee_ids) {
                    const acceptedPayeeIds = response.data[0].accepted_payee_ids.split(",");
                    this.updatePayeeRowsAcceptStatus(acceptedPayeeIds, true);
                }

                this.setButtonsBusy(false);
            } catch (error) {
                Snackbar.showWarningSnackbar(error.messages?.[0] || "Failed to accept payee(s).");
                this.setButtonsBusy(false);
            }
        }
    }

    private async unacceptPayees(unacceptAll?: boolean): Promise<void> {
        this.setButtonsBusy(true);
        let payeeIds: string[] = [];
        let confirmVoid = false;
        if (unacceptAll) {
            const shouldProcess = (modelRow: ModelRow) => {
                const isFrozenPayable = !modelRow.isNull("freeze_by");
                const hasSettlementsOrPayAdjustments = modelRow.get("process_count") > 0;
                const hasProcessStatus = !modelRow.isNull("process_status");
                const hasTimecard = this.payeeDataLookup.getPayeeData(modelRow.get("payee_id"))?.getPayeeRow()?.getCurrency("tc_total_pay")?.isPositive() === true;
                const isCheckPrinted = modelRow.get("process_status") == "C";
                return !isFrozenPayable && hasProcessStatus && !isCheckPrinted && (hasSettlementsOrPayAdjustments || hasTimecard);
            };
            payeeIds = this.tableGroupBy.allRows.filter(row => shouldProcess(row.data)).map(row => row.data.get("payee_id"));
        } else {
            const selectedRow = this.tableGroupBy.selectedRow;
            if (!selectedRow) {
                return;
            }
            payeeIds.push(selectedRow.data.get("payee_id"));
            if (selectedRow.data.get("process_status") === "C") {
                confirmVoid = await CommonDialogs.showYesNo(`A payment has been generated for payee ${selectedRow.data.get("payee_name")}. If you proceed with unaccepting, the unposted payment will be voided, related pay records will be reset, and a payment must be regenerated for the payee. Proceed with unaccept?`, "Void Unposted Payment Confirmation");
                if (!confirmVoid) {
                    this.setButtonsBusy(false);
                    return;
                }
            }
        }
        if (payeeIds.length === 0) {
            Snackbar.showWarningSnackbar("No payees can be unaccepted.");
            this.setButtonsBusy(false);
            return;
        }
        try {
            const response = await Api.post("lme/backoffice/officepayroll/unaccept-payee", {
                payee_ids: payeeIds.join(","),
                confirm_void: confirmVoid
            });

            // Update UI for successfully unaccepted payees
            if (response?.data?.[0]?.unaccepted_payee_ids) {
                const unacceptedPayeeIds = response.data[0].unaccepted_payee_ids.split(",");
                this.updatePayeeRowsAcceptStatus(unacceptedPayeeIds, false);
            }

            this.setButtonsBusy(false);
        } catch (error) {
            Snackbar.showWarningSnackbar(error.messages?.[0] || "Failed to unaccept payee(s).");
            this.setButtonsBusy(false);
        }
    }

    private setAcceptButtonProps(tableRow: TableRow): void {
        if (tableRow) {
            const imageAccepted = tableRow.findComponentById("imageAccepted") as Image;
            if (imageAccepted == null) {
                return;
            }
            const newImageVisibility = !tableRow.data.isNull("process_status");

            // If button was busy and imageAccepted visibility is being updated, clear busy state
            if ((this.buttonAcceptUnaccept.busy || this.buttonEllipsis.busy) && imageAccepted.visible !== newImageVisibility) {
                this.setButtonsBusy(false);
            }

            imageAccepted.visible = newImageVisibility;
            if (this.tableGroupBy.selectedRow === tableRow) {
                const payeeRow = this.payeeDataLookup.getPayeeData(tableRow.data.get("payee_id"))?.getPayeeRow();
                this.buttonAcceptUnaccept.caption = tableRow.data.isNull("process_status") ? "Accept" : "Unaccept";
                this.buttonAcceptUnaccept.enabled = tableRow.data.isNull("freeze_by") && (tableRow.data.get("process_count") > 0 || (this.profileRow.isNull("bonus_code") && payeeRow?.getCurrency("tc_total_pay")?.isPositive() === true));
                this.buttonAcceptUnaccept.disabledTooltip = "Cannot accept payee because it is frozen or has nothing to process.";
                tableRow.contextMenuItems = () => {
                    this.tableGroupBy.selectedRow = tableRow;
                    return [
                        {
                            text: this.buttonAcceptUnaccept.caption,
                            enabled: this.buttonAcceptUnaccept.enabled,
                            onClick: () => {
                                this.buttonAcceptUnaccept.clicked();
                            }
                        }
                    ];
                };
            }
        } else {
            this.buttonAcceptUnaccept.enabled = false;
            this.buttonAcceptUnaccept.disabledTooltip = "Please select a payee to accept or unaccept.";
        }
    }

    private setButtonsBusy(busy: boolean): void {
        this.buttonAcceptUnaccept.busy = busy;
        this.buttonEllipsis.busy = busy;
    }

    /**
     * Updates the accept status and UI for the specified payee rows
     * @param payeeIds Array of payee IDs that were successfully processed
     * @param accepted True if payees were accepted, false if unaccepted
     */
    private updatePayeeRowsAcceptStatus(payeeIds: string[], accepted: boolean): void {
        for (const payeeId of payeeIds) {
            // Find the row in the group by table for this payee
            const tableRow = this.tableGroupBy.allRows.find(row => row.data.get("payee_id") === payeeId);
            if (tableRow) {
                // Update the process_status in the model data
                if (accepted) {
                    tableRow.data.set("process_status", "A"); // Set to accepted status
                } else {
                    tableRow.data.set("process_status", null); // Clear status for unaccept
                }

                // Find and update the accept image visibility
                const imageAccepted = tableRow.findComponentById("imageAccepted") as Image;
                if (imageAccepted) {
                    imageAccepted.visible = accepted;
                }

                // Update button properties if this is the currently selected row
                if (this.tableGroupBy.selectedRow === tableRow) {
                    this.setAcceptButtonProps(tableRow);
                }
            }
        }
    }

    /**
     * Validates that deductions don't exceed gross pay for the given payees
     * @param payeeIds Array of payee IDs to validate
     * @returns Array of validation error messages
     */
    private async validateDeductionsVsGrossPay(payeeIds: string[]): Promise<string[]> {
        const errors: string[] = [];

        try {
            // Get gross pay and deduction totals for all payees in a single loop
            const payeeTotals = await this.getPayeeTotalsOptimized(payeeIds);

            // Validate each payee
            for (const payeeId of payeeIds) {
                const totals = payeeTotals.get(payeeId);
                const groupByRow = this.tableGroupBy.allRows.find(row => row.data.get("payee_id") === payeeId);
                if (!totals || !groupByRow) {
                    continue; // Skip if no data found for this payee
                }

                // Calculate gross pay minus deductions
                const grossLessDeductions = new Currency(totals.grossPay);
                await grossLessDeductions.add(totals.beforeTaxDeductions);
                await grossLessDeductions.add(totals.total401kContributions);


                if (grossLessDeductions.getAmount() < 0 && !groupByRow.data.getCurrency("process_total", 0).isPositive()) {
                    errors.push(
                        `Cannot accept payee ${payeeId} because sum of before-tax deductions and 401(k) is ` +
                        `more than gross pay. You must adjust before-tax deductions before accepting payee.`
                    );
                }
            }
        } catch (error) {
            console.error("Error during validation:", error);
            errors.push(`Error during validation: ${error.message || error}`);
        }

        return errors;
    }

    /**
     * Gets gross pay and deduction totals for all payees in a single loop through the data source
     * @param payeeIds Array of payee IDs to process
     * @returns Map of payee ID to totals (gross pay, before-tax deductions, 401k contributions)
     */
    private async getPayeeTotalsOptimized(payeeIds: string[]): Promise<Map<string, {
        grossPay: Currency,
        beforeTaxDeductions: Currency,
        total401kContributions: Currency
    }>> {
        const payeeTotals = new Map<string, {
            grossPay: Currency,
            beforeTaxDeductions: Currency,
            total401kContributions: Currency
        }>();

        // Initialize totals for each payee
        for (const payeeId of payeeIds) {
            payeeTotals.set(payeeId, {
                grossPay: new Currency(0),
                beforeTaxDeductions: new Currency(0),
                total401kContributions: new Currency(0)
            });
        }

        if (!this.sourceOrderPayablesCache || !this.sourceOrderPayablesCache.data) {
            return payeeTotals;
        }

        // Single loop through the data source to process all records
        for (const row of this.sourceOrderPayablesCache.data) {
            const payeeId = row.get("payee_id");

            // Only process records for the payees we're interested in
            if (!payeeIds.includes(payeeId)) {
                continue;
            }

            const totals = payeeTotals.get(payeeId);
            if (!totals) {
                continue;
            }

            const recordType = row.get("record_type");
            const readyToPayFlag = row.getString("ready_to_pay_flag");

            if (recordType === "O" && readyToPayFlag === "Y") {
                const deductCodeId = row.getString("deduct_code_id");
                if (deductCodeId) {
                    const amount = row.getCurrency("pay_amount", new Currency(0));

                    const deductCodeDesc = row.getString("deduct_code_descr", "");
                    // Check for before-tax deductions
                    if (row.getString("deduct_type") === "B") { // "B" is DEDUCT_TYPE_BEFORETAX
                        await totals.beforeTaxDeductions.add(amount);
                    } else if (deductCodeId.includes("401") || deductCodeDesc.toLowerCase().includes("401")) {
                        await totals.total401kContributions.add(amount);
                    }
                }
            }
        }
        return payeeTotals;
    }
}
